import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';

const AuthContext = createContext(null);  

// Configuration constants
const CONFIG = {
  API_BASE_URL: (process.env.REACT_APP_API_URL || 'http://localhost:8000'),
  PUBLIC_ROUTES: ['/login', '/register', '/forgot-password', '/reset-password'],
  TOKEN_EXPIRY_BUFFER: 5 * 60,
  SESSION_HEARTBEAT_INTERVAL: 60000,
  MAX_REFRESH_RETRIES: 3,
  REFRESH_RETRY_DELAY: 1000,
  DEBUG: false
};

class AuthError extends Error {
  constructor(message, code, details = null) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
    this.details = details;
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const refreshTimeoutId = useRef(null);
  const isRefreshing = useRef(false);
  const pendingRequests = useRef([]);
  const mounted = useRef(false);
  const initialized = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  const api = axios.create({
    baseURL: CONFIG.API_BASE_URL,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
  });

  const clearAuth = useCallback(() => {
    setUser(null);
    setAccessToken(null);
    setAuthError(null);
    if (refreshTimeoutId.current) {
      clearTimeout(refreshTimeoutId.current);
      refreshTimeoutId.current = null;
    }
    localStorage.removeItem('lastLoginTime');
  }, []);

  const handleSessionExpiration = useCallback(async () => {
    clearAuth();
    setAuthError('Your session has expired. Please log in again.');
    navigate('/login', {
      state: {
        from: location.pathname,
        sessionExpired: true
      },
      replace: true
    });
  }, [navigate, location.pathname, clearAuth]);

  const extractUserFromToken = useCallback((token, responseData = {}) => {
    if (!token) {
      return null;
    }

    try {
      const tokenStr = token.replace(/^Bearer\s+/i, '');
      const payload = JSON.parse(atob(tokenStr.split('.')[1]));

      const userData = {
        id: payload.sub || payload.user_id,
        email: payload.email,
        role: payload.role,
        school_id: payload.school_id,
        name: payload.name,
        school_reg: payload.school_reg ||
                   responseData?.user?.school_registration ||
                   responseData?.user?.school_reg ||
                   payload.school_registration ||
                   responseData?.school_registration ||
                   payload.school_reg_number,
        exp: payload.exp,
        permissions: payload.permissions || [],
        name: payload.name || responseData?.user?.name,
        last_active: new Date().toISOString()
      };

      const requiredFields = ['id', 'email', 'role'];
      const missingFields = requiredFields.filter(field => !userData[field]);

      if (missingFields.length > 0) {
        throw new AuthError(
          `Missing required user data: ${missingFields.join(', ')}`,
          'INVALID_TOKEN_DATA'
        );
      }

      return userData;
    } catch (err) {
      throw new AuthError(
        'Failed to extract user data from token',
        'TOKEN_EXTRACTION_ERROR',
        err
      );
    }
  }, []);

  const isTokenExpired = useCallback((token) => {
    if (!token) return true;

    try {
      const userData = extractUserFromToken(token);
      if (!userData?.exp) return true;

      const now = Math.floor(Date.now() / 1000);
      const isExpired = now >= userData.exp - CONFIG.TOKEN_EXPIRY_BUFFER;

      return isExpired;
    } catch (err) {
      return true;
    }
  }, [extractUserFromToken]);

  const refreshToken = useCallback(async () => {
    if (!mounted.current) return;
    if (isRefreshing.current) {
      return new Promise((resolve, reject) => {
        pendingRequests.current.push({ resolve, reject });
      });
    }
    
    let retries = CONFIG.MAX_REFRESH_RETRIES;
    while (retries > 0 && mounted.current) {
      try {
        isRefreshing.current = true;

        const response = await api.post('/api/v1/auth/refresh-token');
        const { access_token } = response.data;

        if (!access_token) {
          throw new AuthError('No token received from refresh', 'REFRESH_TOKEN_MISSING');
        }

        const userData = extractUserFromToken(access_token, response.data);
        if (!userData) {
          throw new AuthError('Invalid token data after refresh', 'INVALID_REFRESH_TOKEN');
        }

        if (mounted.current) {
          setAccessToken(access_token);
          setUser(userData);

          const timeUntilRefresh = (userData.exp - CONFIG.TOKEN_EXPIRY_BUFFER - Math.floor(Date.now() / 1000)) * 1000;
          if (timeUntilRefresh > 0 && mounted.current) {
            if (refreshTimeoutId.current) {
              clearTimeout(refreshTimeoutId.current);
            }
            refreshTimeoutId.current = setTimeout(() => refreshToken(), timeUntilRefresh);
          }

          pendingRequests.current.forEach(request => request.resolve(access_token));
          pendingRequests.current = [];

          return access_token;
        }
      } catch (err) {
        const isSessionError = err.response?.data?.detail === 'session expired' || 
                             err.response?.data?.detail === 'Session not found';

        if (isSessionError || err.response?.status === 401 || retries === 1) {
          pendingRequests.current.forEach(request => request.reject(err));
          pendingRequests.current = [];
          
          if (isSessionError && mounted.current) {
            await handleSessionExpiration();
          }
          throw err;
        }

        retries--;
        await new Promise(resolve => setTimeout(resolve, CONFIG.REFRESH_RETRY_DELAY));
      } finally {
        isRefreshing.current = false;
      }
    }
  }, [api, extractUserFromToken, handleSessionExpiration]);

  const logout = useCallback(async (redirectPath = '/login') => {
    try {
      await api.post('/api/v1/auth/logout');
    } catch (err) {
    } finally {
      clearAuth();
      navigate(redirectPath, { replace: true });
    }
  }, [api, navigate, clearAuth]);

  const login = async (email, password) => {
    if (!mounted.current) return;
    setLoading(true);
    setAuthError(null);
    clearAuth();
  
    try {
      const deviceInfo = {
        user_agent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        screen_resolution: `${window.screen.width}x${window.screen.height}`
      };

      const response = await api.post('/api/v1/auth/login', { 
        email, 
        password,
        device_info: deviceInfo
      });
  
      const { access_token } = response.data;
      
      if (!access_token) {
        throw new AuthError('Invalid response from login', 'LOGIN_RESPONSE_INVALID');
      }
  
      const userData = extractUserFromToken(access_token, response.data);
  
      if (!userData.school_reg) {
        throw new AuthError(
          'School registration number missing from token and user data',
          'MISSING_SCHOOL_REG'
        );
      }

      if (mounted.current) {
        setAccessToken(access_token);
        setUser(userData);
        localStorage.setItem('lastLoginTime', new Date().toISOString());

        const timeUntilRefresh = (userData.exp - CONFIG.TOKEN_EXPIRY_BUFFER - Math.floor(Date.now() / 1000)) * 1000;
        if (timeUntilRefresh > 0 && mounted.current) {
          refreshTimeoutId.current = setTimeout(() => refreshToken(), timeUntilRefresh);
        }

        const savedPath = location.state?.from || '/dashboard';
        navigate(savedPath, { replace: true });
      }
      
      return userData;
    } catch (err) {
      if (!mounted.current) return;

      const handleLoginError = (status, defaultMessage) => {
        const errorMap = {
          429: 'Too many login attempts. Please try again later.',
          423: 'Account locked. Please try again later.',
          401: 'Invalid email or password.',
          404: 'Account not found. Please check your email.',
          403: 'Your account has been disabled. Please contact support.'
        };

        return errorMap[status] || defaultMessage;
      };

      const message = err.response?.data?.detail || 
                     err.response?.data?.message || 
                     err.message;

      const errorMessage = !navigator.onLine 
        ? 'No internet connection. Please check your network.'
        : err.message.includes('School registration')
        ? 'Account setup incomplete. Please contact your administrator.'
        : handleLoginError(err.response?.status, message || 'Login failed');

      setAuthError(errorMessage);
      
      throw err;
    } finally {
      if (mounted.current) {
        setLoading(false);
      }
    }
  };

  const updateUserFromResetPassword = useCallback(async (tokenData) => {
    if (!mounted.current) return;
    
    try {
      const { access_token, user } = tokenData;
      
      if (!access_token) {
        throw new AuthError('Invalid token data from reset password', 'RESET_TOKEN_INVALID');
      }
      
      const userData = extractUserFromToken(access_token, { user });
      
      if (mounted.current) {
        setAccessToken(access_token);
        setUser(userData);
        localStorage.setItem('lastLoginTime', new Date().toISOString());
        
        const timeUntilRefresh = (userData.exp - CONFIG.TOKEN_EXPIRY_BUFFER - Math.floor(Date.now() / 1000)) * 1000;
        if (timeUntilRefresh > 0 && mounted.current) {
          if (refreshTimeoutId.current) {
            clearTimeout(refreshTimeoutId.current);
          }
          refreshTimeoutId.current = setTimeout(() => refreshToken(), timeUntilRefresh);
        }
        
        return userData;
      }
    } catch (err) {
      throw err;
    }
  }, [extractUserFromToken, refreshToken]);

  useEffect(() => {
    mounted.current = true;

    const initAuth = async () => {
      if (initialized.current || CONFIG.PUBLIC_ROUTES.includes(location.pathname)) {
        setLoading(false);
        return;
      }

      try {
        const lastLogin = localStorage.getItem('lastLoginTime');
        if (lastLogin) {
          await refreshToken();
        }
      } catch (err) {
      } finally {
        if (mounted.current) {
          setLoading(false);
          initialized.current = true;
        }
      }
    };

    initAuth();

    return () => {
      mounted.current = false;
      if (refreshTimeoutId.current) {
        clearTimeout(refreshTimeoutId.current);
      }
    };
  }, []); 

  useEffect(() => {
    const requestInterceptor = api.interceptors.request.use(
      async config => {
        if (!mounted.current) return config;
        
        if (config.url?.includes('/auth/') || CONFIG.PUBLIC_ROUTES.includes(location.pathname)) {
          return config;
        }

        if (accessToken && !isTokenExpired(accessToken)) {
          config.headers.Authorization = `Bearer ${accessToken}`;
          return config;
        }

        try {
          const newToken = await refreshToken();
          config.headers.Authorization = `Bearer ${newToken}`;
          return config;
        } catch (err) {
          throw err;
        }
      },
      err => Promise.reject(err)
    );

    return () => api.interceptors.request.eject(requestInterceptor);
  }, [api.interceptors.request, location.pathname, refreshToken, accessToken, isTokenExpired]);

  useEffect(() => {
    const responseInterceptor = api.interceptors.response.use(
      response => response,
      async err => {
        if (!mounted.current) return Promise.reject(err);
        const originalRequest = err.config;
        
        if (err.response?.status === 401) {
          const errorDetail = err.response?.data?.detail;
          
          if (errorDetail === 'session expired' || errorDetail === 'Session not found') {
            await handleSessionExpiration();
            throw err;
          }
          
          if (!originalRequest._retry) {
            originalRequest._retry = true;
            try {
              const newToken = await refreshToken();
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return api(originalRequest);
            } catch (refreshError) {
              throw refreshError;
            }
          }
        }
        
        if (err.response?.status === 500) {
          throw new AuthError(
            'A server error occurred. Please try again later.',
            'SERVER_ERROR',
            err.response?.data
          );
        }
        
        return Promise.reject(err);
      }
    );

    return () => api.interceptors.response.eject(responseInterceptor);
  }, [api.interceptors.response, refreshToken, handleSessionExpiration]);

  useEffect(() => {
    if (!user || !mounted.current) return;
    
    const checkSession = async () => {
      if (!mounted.current) return;
      try {
        await api.post('/api/v1/auth/verify-session');
      } catch (err) {
        if (err.response?.status === 401) {
          await handleSessionExpiration();
        }
      }
    };

    const intervalId = setInterval(checkSession, CONFIG.SESSION_HEARTBEAT_INTERVAL);
    return () => clearInterval(intervalId);
  }, [user, api, handleSessionExpiration]);

  const contextValue = {
    user,
    loading,
    error: authError,
    login,
    logout,
    setError: setAuthError,
    isAuthenticated: !!user,
    updateUserFromResetPassword,
    api,
    refreshToken,
    clearError: useCallback(() => setAuthError(null), [])
  };

  if (loading && !CONFIG.PUBLIC_ROUTES.includes(location.pathname)) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthProvider;